import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { ChevronLeft, ChevronRight, Play, X } from "react-feather";
import { Banner, Button, Checkbox } from "@cinesys/ui/components";
import { SPOTTER_STRINGS } from "../../strings";
import { nextVideoLoadState, VideoLoadState } from "../videoLoad";
import { useInView } from "../useInView";
import {
  AnchorTask,
  useClipThumbnail,
  useNeighborClip,
  useProxyUrl,
} from "../service";

/**
 * The stage band (full-page anchor editor, Option A) — video fixed on the left, everything else
 * (state/mode chips, "ANCHOR n / N", the prompt, the out-of-order banner, the clip-nav widget,
 * the manual-UID escape hatch) to its right. ONE instance mounts at a time, for the current
 * anchor — `key={anchor.anchorId}` at the call site (`AnchorConfirmContent.tsx`) remounts this
 * fresh on every anchor switch, which is deliberately safe: everything this component owns
 * LOCALLY (video load state, the fetched neighbor title, manual-UID field) is per-anchor
 * transient UI state, NOT the customer's confirmed-but-unsubmitted resolutions — those live one
 * level up, in `AnchorConfirmContent.tsx`'s own draft map (`anchorResolutionDraft.ts`), which
 * this remount never touches (see that file's doc comment on why the invariant still holds) and
 * which this component reads back every render as `resolvedClipAssetId`, not just at mount.
 *
 * Picking a play happens in the sibling `AnchorGamebookTable` band below (including its own
 * header's manual-UID escape hatch, `AnchorConfirmContent.tsx` owns that commit path directly) —
 * this component never commits a PLAY itself. It DOES own the clip substitution: stepping the
 * reel to a different clip IS the substitution, fired via `onSubstitute` the moment a stepped-to
 * clip is known — no separate confirm step, and no "just previewing" state in between. That
 * timing is also what makes a gamebook-row (or manual-UID) commit always attribute to the right
 * clip: by the time a play click could happen, the clip it's attributed against
 * (`AnchorConfirmContent.tsx`'s own draft state) is already current. `onUndoSubstitution` is the
 * one remaining explicit action — the way back to Spotter's own suggested clip.
 *
 * `isStartFrame` is set when `AnchorConfirmContent.tsx` is showing the synthetic Game Start
 * pseudo-anchor rather than a real one — Spotter doesn't let this screen change the start clip,
 * so the anchor counter/stepper, the "pick a play" prompt, and the clip-nav widget (which exists
 * to substitute a different clip for a real anchor) are all hidden; only the video itself is
 * shown, read-only.
 */

const StageRow = styled.div`
  display: flex;
  gap: 14px;
  flex: 0 1 292px;
  min-height: 180px;
`;

const PLAYER_WIDTH = 512;

const Player = styled.div`
  flex: 0 0 ${PLAYER_WIDTH}px;
  position: relative;
  border-radius: 9px;
  overflow: hidden;
  background: #000;
  border: 1px solid var(--spotterLine2);
`;

const Thumb = styled.button<{ $posterUrl?: string }>`
  all: unset;
  width: 100%;
  height: 100%;
  box-sizing: border-box;
  cursor: pointer;
  display: grid;
  place-items: center;
  color: var(--spotterInkSoft);
  background: ${(p) =>
    p.$posterUrl
      ? `linear-gradient(rgba(0, 0, 0, 0.15), rgba(0, 0, 0, 0.35)), url("${p.$posterUrl}") center / cover no-repeat`
      : `radial-gradient(120% 120% at 30% 20%, var(--spotterRaised), var(--spotterSlate))`};

  &:hover {
    color: var(--spotterAccent);
  }
`;

/** Contrast scrim behind the play glyph — needed once a real keyframe image is showing through
 *  `Thumb` (unnecessary, but harmless, over the plain placeholder gradient). */
const PlayBadge = styled.span`
  display: grid;
  place-items: center;
  width: 52px;
  height: 52px;
  border-radius: 50%;
  background: rgba(6, 10, 12, 0.55);
  color: #fff;
`;

const VideoEl = styled.video`
  width: 100%;
  height: 100%;
  display: block;
  object-fit: contain;
  background: #000;
`;

const CloseVideoButton = styled.button`
  all: unset;
  position: absolute;
  top: 6px;
  right: 6px;
  width: 26px;
  height: 26px;
  border-radius: 7px;
  cursor: pointer;
  border: 1px solid rgba(255, 255, 255, 0.25);
  background: rgba(6, 10, 12, 0.6);
  color: #fff;
  display: grid;
  place-items: center;
  z-index: 1;
`;

const ClipNameTag = styled.span`
  position: absolute;
  top: 10px;
  left: 12px;
  font: 400 10.5px/1 ui-monospace, Menlo, monospace;
  color: rgba(255, 255, 255, 0.6);
`;

const Meta = styled.div`
  flex: 1;
  min-width: 0;
  background: var(--spotterSurface);
  border: 1px solid var(--spotterLine);
  border-radius: 10px;
  padding: 14px 16px;
  display: flex;
  flex-direction: column;
  gap: 11px;
  overflow-y: auto;
`;

const Chips = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const CHIP_TONE = {
  neutral: {
    background: "var(--spotterRaised)",
    color: "var(--spotterInkSoft)",
    border: "var(--spotterLine2)",
  },
  accent: {
    background: "var(--spotterAccentSoft)",
    color: "var(--spotterAccent)",
    border: "var(--spotterAccentLine)",
  },
  success: {
    background: "var(--spotterOkSoft)",
    color: "var(--spotterOk)",
    border: "var(--spotterOkLine)",
  },
} as const;

const Chip = styled.span<{ $tone?: keyof typeof CHIP_TONE }>`
  font-size: 10.5px;
  padding: 4px 9px;
  border-radius: 999px;
  white-space: nowrap;
  background: ${(p) => CHIP_TONE[p.$tone ?? "accent"].background};
  color: ${(p) => CHIP_TONE[p.$tone ?? "accent"].color};
  border: 1px solid ${(p) => CHIP_TONE[p.$tone ?? "accent"].border};
`;

const CounterNav = styled.div`
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const CounterArrowButton = styled.button`
  all: unset;
  width: 20px;
  height: 20px;
  border-radius: 6px;
  border: 1px solid var(--spotterLine);
  background: var(--spotterSlate);
  color: var(--spotterInk);
  cursor: pointer;
  display: grid;
  place-items: center;

  &:hover:not(:disabled) {
    border-color: var(--spotterAccentLine);
    color: var(--spotterAccent);
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const Counter = styled.span`
  font: 500 11.5px/1 ui-monospace, Menlo, monospace;
  color: var(--spotterInkFaint);
`;

const Prompt = styled.p`
  font-size: 13px;
  color: var(--spotterInkSoft);
  line-height: 1.5;
  max-width: 60ch;
  margin: 0;
`;

/** The "nice little widget" for stepping through the reel around the current anchor and,
 *  optionally, substituting a different clip for it. Rebuilt (Addison: "it just looks messy...
 *  different font sizes, the checkbox being in a weird spot, some things are centered some
 *  aren't... less text in its different states") as ONE consistently-centered column: the arrow
 *  row, a single small caption line, and a single small utility row below it — every text element
 *  in the card other than the arrow-row label itself now shares the same small/muted size
 *  (`CLIP_NAV_SMALL_FONT_PX`), and every row centers its own content rather than some rows
 *  centering and others space-between-ing. Pinned to the bottom of `Meta` via `margin-top: auto`,
 *  same as the strip it replaces. */
const CLIP_NAV_SMALL_FONT_PX = 11;

const ClipNavCard = styled.div`
  margin-top: auto;
  border: 1px solid var(--spotterLine);
  border-radius: 10px;
  background: var(--spotterRaised);
  padding: 10px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 7px;
`;

/** Pill-shaped (Addison: "for the left-right thing with the text in there... maybe it should
 *  have like a background or border... pill shape") — one bordered capsule holding both arrows
 *  and the label between them, instead of the label floating free with only the two separately-
 *  bordered arrow buttons giving the row any visual shape. */
const ClipNavRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  width: 100%;
  padding: 4px;
  border-radius: 999px;
  border: 1px solid var(--spotterLine);
  background: var(--spotterSlate);
`;

const ClipNavButton = styled.button`
  all: unset;
  flex: none;
  width: 26px;
  height: 26px;
  border-radius: 999px;
  color: var(--spotterInk);
  cursor: pointer;
  display: grid;
  place-items: center;
  transition: background-color 0.13s, color 0.13s;

  &:hover:not(:disabled) {
    background: var(--spotterRaised);
    color: var(--spotterAccent);
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const ClipNavLabel = styled.span`
  flex: 1;
  min-width: 0;
  text-align: center;
  font-size: 12.5px;
  font-weight: 700;
  color: var(--spotterInk);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

/** The one explanatory line — small/muted, centered, same size as `ClipNavUtilityRow`'s own text
 *  below it (no separate "boundary hint"/"substituted hint" sentences anymore; a disabled arrow's
 *  own `title` tooltip and the arrow-row label already say those, see the render logic below). */
const ClipNavCaption = styled.p`
  font-size: ${CLIP_NAV_SMALL_FONT_PX}px;
  color: var(--spotterInkFaint);
  margin: 0;
  text-align: center;
`;

/** The widget's one row of secondary controls — "Show all clips" and (only when substituted) the
 *  revert action, centered together as a group instead of one being pinned to a corner. */
const ClipNavUtilityRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  flex-wrap: wrap;
`;

/** `Checkbox`'s own inline label renders at 14.5px (`@cinesys/ui`'s `Label.tsx`) — visibly bigger
 *  than everything else in this card; this brings it down to the same small size as
 *  `ClipNavCaption`/the revert button so the row doesn't read as two different type scales.
 *  `--spotterInk` here, not the fainter `--spotterInkFaint` used elsewhere in this card — the
 *  unchecked `<label>` already dims itself to 60% opacity (`Checkbox.tsx`'s own `StyledCheckbox`
 *  rule), so stacking a faint color UNDER that opacity read as too dark/low-contrast (Addison:
 *  "the font color on the checkbox is a bit dark"). */
const CompactCheckbox = styled(Checkbox)`
  label {
    font-size: ${CLIP_NAV_SMALL_FONT_PX}px;
    color: var(--spotterInk);
  }
`;

export interface AnchorStageProps {
  jobId: string;
  anchor: AnchorTask;
  totalAnchors: number;
  isSlowmoBand: boolean;
  /** Only the COUNT is used here (the stage's own banner, "N anchors out of order") — WHICH
   *  anchors are out of order is the reel strip's own concern (`ReelStrip.tsx`'s per-card ring). */
  outOfOrderCount: number;
  locked: boolean;
  /** Whether the CURRENT anchor already has a draft resolution (`AnchorConfirmContent.tsx`'s own
   *  draft map, `anchorResolutionDraft.ts`) — distinct from `locked` (whole-job terminal state):
   *  this can be `true` while `locked` is still `false`, e.g. right after picking a play but
   *  before submitting the whole collection. Swaps the `Chips` row's pending/confirmed badge. */
  resolved: boolean;
  /** The clip this anchor is CURRENTLY committed to (`anchorResolutionDraft.ts`'s
   *  `effectiveClipAssetId`), `null` when it's still on its own suggested clip — read on every
   *  render, not just at mount, so this drives `activeClipAssetId`/the "Substituted" badge
   *  directly instead of a local copy that could drift from what's actually stored. */
  resolvedClipAssetId: string | null;
  /** Fires the moment stepping the reel lands on a different clip — that IS the substitution,
   *  no separate confirm step. See `anchorResolutionDraft.ts`'s `applyClipSubstitution` for what
   *  the orchestrator does with it — including attributing the next play/manual-UID commit to
   *  this clip, since that state is already updated by the time a commit could happen. */
  onSubstitute: (clipAssetId: string) => void;
  /** The one remaining explicit action — "Use Spotter's original clip" — restores the anchor's
   *  own suggested clip. */
  onUndoSubstitution: () => void;
  /** True when `anchor` is the synthetic Game Start pseudo-anchor — this screen can't change the
   *  start clip, so the anchor counter/stepper, the prompt, and the clip-nav widget are hidden. */
  isStartFrame: boolean;
  onPrevAnchor: () => void;
  onNextAnchor: () => void;
  hasPrevAnchor: boolean;
  hasNextAnchor: boolean;
}

const AnchorStage = ({
  jobId,
  anchor,
  totalAnchors,
  isSlowmoBand,
  outOfOrderCount,
  locked,
  resolved,
  resolvedClipAssetId,
  onSubstitute,
  onUndoSubstitution,
  isStartFrame,
  onPrevAnchor,
  onNextAnchor,
  hasPrevAnchor,
  hasNextAnchor,
}: AnchorStageProps) => {
  const t = SPOTTER_STRINGS.filmsGround.phaseB;
  const [loadState, setLoadState] = useState<VideoLoadState>("not-loaded");
  const [loadTrigger, setLoadTrigger] = useState<"scroll" | "click" | null>(
    null
  );
  // The friendly name of whatever clip is currently substituted in, for the nav label — kept
  // purely for display. `null` whenever the anchor is on its own suggested clip, OR when it's on
  // a substituted clip this component didn't itself step to this session (reopening an anchor
  // that was already substituted before — the stored state only carries an asset id, not a
  // title); the label falls back to a generic string in that case rather than lying with a stale
  // title (see `ClipNavLabel`'s render logic below).
  const [viewedTitle, setViewedTitle] = useState<string | null>(null);
  const [boundaryDirection, setBoundaryDirection] = useState<
    "prev" | "next" | null
  >(null);
  const [walk, setWalk] = useState<{
    fromAssetId: string;
    direction: "prev" | "next";
  } | null>(null);
  const [showAllClips, setShowAllClips] = useState(false);

  // Controlled by the orchestrator, not local state — `resolvedClipAssetId` is recomputed from
  // stored draft state on every render (`AnchorConfirmContent.tsx`'s `anchorResolutionDraft.ts`
  // usage), so reading it directly here is what makes the "Substituted" badge reflect what's
  // actually stored instead of a local copy that only this component's own clicks could update.
  // Shifting clips automatically changes the anchor's clip — stepping the reel (below) commits
  // immediately, so there is no separate "previewing" clip distinct from this one.
  const activeClipAssetId = resolvedClipAssetId ?? anchor.clipAssetId;
  const isSubstituted = activeClipAssetId !== anchor.clipAssetId;

  // Ref, not a dep — reports the LATEST callback without re-firing the effect just because the
  // orchestrator handed in a new inline closure on re-render (same pattern `useInView.ts`'s
  // `onEnterRef` already uses in this feature).
  const onSubstituteRef = useRef(onSubstitute);
  onSubstituteRef.current = onSubstitute;

  const proxyQuery = useProxyUrl(jobId, activeClipAssetId, {
    enabled: loadState !== "not-loaded",
  });

  // Pre-load poster — the thumbnail, not the video proxy, so it's cheap to fetch unconditionally
  // for whichever single clip is on screen (unlike `ReelStrip.tsx`'s reel, this is only ever ONE
  // clip at a time). Not needed once the real video is already showing.
  const posterQuery = useClipThumbnail(jobId, activeClipAssetId, {
    enabled: loadState === "not-loaded",
  });

  useEffect(() => {
    if (proxyQuery.data) {
      setLoadState((s) => nextVideoLoadState(s, "proxy-ready"));
    }
  }, [proxyQuery.data]);

  const neighborQuery = useNeighborClip(
    jobId,
    walk?.fromAssetId,
    walk?.direction ?? "next",
    showAllClips,
    { enabled: !!walk }
  );
  const stepPending = !!walk && neighborQuery.isFetching;

  // React-query v3's `onQuery.onSuccess` only fires on an actual fetch dispatch — re-enabling a
  // query whose key is already cached and fresh (e.g. stepping next, then back to the clip you
  // started from, then next again) resolves `data` from cache with NO dispatch, so `onSuccess`
  // silently never runs and the arrows appear to "stop working". Driving this off `walk` itself
  // (a fresh object every click) instead guarantees the effect re-evaluates `neighborQuery.data`
  // on every step, cache hit or not.
  //
  // Landing on a new clip here IS the substitution — no separate confirm step. Picking a play
  // always reads the anchor's CURRENT clip from stored draft state
  // (`AnchorConfirmContent.tsx`'s `handleCommit`), and that state is already updated by the time
  // a play click could possibly happen, because it happens right here, the moment the stepped-to
  // clip is known — not deferred to an explicit button.
  useEffect(() => {
    if (!walk || neighborQuery.data === undefined) {
      return;
    }
    if (neighborQuery.data) {
      setViewedTitle(neighborQuery.data.title);
      setBoundaryDirection(null);
      onSubstituteRef.current(neighborQuery.data.assetId);
    } else {
      setBoundaryDirection(walk.direction);
    }
  }, [walk, neighborQuery.data]);

  const resetNeighborState = () => {
    setViewedTitle(null);
    setWalk(null);
    setBoundaryDirection(null);
  };

  const requestStep = (direction: "prev" | "next") => {
    if (stepPending) {
      return;
    }
    requestLoad("click");
    setWalk({ fromAssetId: activeClipAssetId, direction });
  };

  const revertToOriginal = () => {
    onUndoSubstitution();
    resetNeighborState();
  };

  const requestLoad = (trigger: "scroll" | "click") => {
    if (loadState !== "not-loaded") {
      return;
    }
    resetNeighborState();
    setLoadTrigger(trigger);
    setLoadState(nextVideoLoadState(loadState, trigger));
  };

  const videoAreaRef = useInView<HTMLDivElement>(() => requestLoad("scroll"));

  const closeVideo = () => {
    setLoadState("not-loaded");
    setLoadTrigger(null);
    resetNeighborState();
  };

  return (
    <StageRow>
      <Player ref={videoAreaRef}>
        {/* Always the clip actually on screen, not the anchor's own suggestion — the anchor's
            clip should visibly be whatever is being viewed. Falls back to the bare asset id only
            when substituted to a clip this component didn't itself step to this session (no
            fetched title to show yet). */}
        <ClipNameTag>
          {isSubstituted
            ? viewedTitle ?? activeClipAssetId
            : anchor.clipName || anchor.clipAssetId}
        </ClipNameTag>
        {loadState !== "not-loaded" ? (
          <>
            <CloseVideoButton
              type="button"
              aria-label={t.closeVideo}
              onClick={closeVideo}
            >
              <X size={14} />
            </CloseVideoButton>
            {proxyQuery.data && (
              <VideoEl
                key={activeClipAssetId}
                controls
                autoPlay={loadTrigger === "click"}
                playsInline
                preload="metadata"
                src={proxyQuery.data.url}
              />
            )}
          </>
        ) : (
          <Thumb
            type="button"
            aria-label={t.openVideo}
            onClick={() => requestLoad("click")}
            $posterUrl={posterQuery.data?.url}
          >
            <PlayBadge>
              <Play size={22} />
            </PlayBadge>
          </Thumb>
        )}
      </Player>

      <Meta>
        <Chips>
          {isStartFrame ? (
            <Chip $tone="accent">{t.startFrameLabel}</Chip>
          ) : (
            <>
              {!locked &&
                (resolved ? (
                  <Chip $tone="success">{t.confirmedSectionLabel}</Chip>
                ) : (
                  <Chip>{t.pendingSectionLabel}</Chip>
                ))}
              <Chip $tone="neutral">
                {isSlowmoBand
                  ? SPOTTER_STRINGS.filmsGround.phaseBSlowmoBand.bandBadge
                  : t.modeChipLabel}
              </Chip>
              <CounterNav>
                <CounterArrowButton
                  type="button"
                  aria-label={t.neighborPrevAnchorAria}
                  disabled={!hasPrevAnchor}
                  onClick={onPrevAnchor}
                >
                  <ChevronLeft size={13} />
                </CounterArrowButton>
                <Counter>
                  {t.anchorCounter(anchor.anchorId, totalAnchors)}
                </Counter>
                <CounterArrowButton
                  type="button"
                  aria-label={t.neighborNextAnchorAria}
                  disabled={!hasNextAnchor}
                  onClick={onNextAnchor}
                >
                  <ChevronRight size={13} />
                </CounterArrowButton>
              </CounterNav>
            </>
          )}
        </Chips>

        {!isStartFrame && <Prompt>{t.anchorPrompt}</Prompt>}

        {!isStartFrame && outOfOrderCount > 0 && (
          <Banner error={t.outOfOrderBanner(outOfOrderCount)} />
        )}

        {!isStartFrame && (
          <ClipNavCard>
            <ClipNavCaption>{t.neighborStepperHint}</ClipNavCaption>

            <ClipNavRow>
              <ClipNavButton
                type="button"
                aria-label="Show the previous clip in this reel"
                title={
                  boundaryDirection === "prev"
                    ? t.neighborNoEarlierClip
                    : undefined
                }
                disabled={boundaryDirection === "prev" || stepPending}
                onClick={() => requestStep("prev")}
              >
                <ChevronLeft size={14} />
              </ClipNavButton>
              <ClipNavLabel>
                {stepPending
                  ? t.neighborLoadingLabel
                  : isSubstituted
                  ? viewedTitle ?? t.neighborSubstitutedLabel
                  : t.neighborOwnClipLabel}
              </ClipNavLabel>
              <ClipNavButton
                type="button"
                aria-label="Show the next clip in this reel"
                title={
                  boundaryDirection === "next"
                    ? t.neighborNoLaterClip
                    : undefined
                }
                disabled={boundaryDirection === "next" || stepPending}
                onClick={() => requestStep("next")}
              >
                <ChevronRight size={14} />
              </ClipNavButton>
            </ClipNavRow>

            {/* Stepping the reel already IS the substitution — "Show all clips" and the way back
                to Spotter's own suggestion are this widget's only other actions, so they share
                one centered row instead of the checkbox living apart from everything else. */}
            {!locked && (
              <ClipNavUtilityRow>
                <CompactCheckbox
                  inlineLabel
                  noPadding
                  label={t.neighborShowAllToggle}
                  value={showAllClips}
                  onChange={setShowAllClips}
                  disabled={stepPending}
                />
                {!stepPending && isSubstituted && (
                  <Button
                    label={t.neighborUndoSubstitution}
                    type="default"
                    onClick={revertToOriginal}
                  />
                )}
              </ClipNavUtilityRow>
            )}
          </ClipNavCard>
        )}
      </Meta>
    </StageRow>
  );
};

export default AnchorStage;
